import { apiClient } from './client';
import type { Subset, SubsetWithSongs, SpecialCategory } from '../types';

export const subsetsApi = {
  /**
   * Get all subsets (filtered by admin status on backend)
   */
  async getAllSubsets(): Promise<Subset[]> {
    return apiClient.get<Subset[]>('/api/subsets');
  },

  /**
   * Get a specific subset with song IDs
   */
  async getSubsetById(id: number): Promise<SubsetWithSongs> {
    return apiClient.get<SubsetWithSongs>(`/api/subsets/${id}`);
  },

  /**
   * Create a new subset (admin only)
   */
  async createSubset(name: string, description?: string): Promise<Subset> {
    return apiClient.post<Subset>('/api/subsets', { name, description });
  },

  /**
   * Update a subset (admin only)
   */
  async updateSubset(
    id: number,
    updates: {
      name?: string;
      description?: string;
      votingOpen?: boolean;
      showLiveTally?: boolean;
      displayOrder?: number;
      adminOnly?: boolean;
      voteLimit?: number;
      downloadable?: boolean;
      specialCategories?: SpecialCategory[];
    }
  ): Promise<Subset> {
    return apiClient.patch<Subset>(`/api/subsets/${id}`, updates);
  },

  /**
   * Delete a subset (admin only)
   */
  async deleteSubset(id: number): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(`/api/subsets/${id}`);
  },

  /**
   * Replace all songs in a subset (admin only)
   */
  async replaceSubsetSongs(
    id: number,
    songIds: number[]
  ): Promise<{ success: boolean; songCount: number }> {
    return apiClient.put<{ success: boolean; songCount: number }>(
      `/api/subsets/${id}/songs`,
      { songIds }
    );
  },

  /**
   * Remove a song from a subset (admin only)
   */
  async removeSongFromSubset(
    subsetId: number,
    songId: number
  ): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(
      `/api/subsets/${subsetId}/songs/${songId}`
    );
  },

  /**
   * Download subset songs as ZIP file
   */
  async downloadSubset(id: number, subsetName: string): Promise<void> {
    const response = await fetch(`/api/subsets/${id}/download`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Download failed');
    }

    // Create blob from response
    const blob = await response.blob();

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const sanitizedName = subsetName
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase();
    a.download = `${sanitizedName}-songs.zip`;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};
